import React, { useState, useEffect, useMemo,useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { aboutData, foodItems, rooms, galleryImages, destinationEvents, allEventTypes } from '../../data/dreamlandData';
import { Wifi, Wind, Eye, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import BookingModal from '../dreamland/BookingModal';





const SectionTitle = ({ title, subtitle, light }) => (
  <div className="text-center mb-12">
    <h2
      className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2"
      style={{ fontFamily: "'Playfair Display', Georgia, serif", color: light ? '#FFFFFF' : '#1B3D6E' }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        className="text-xl mt-1"
        style={{ fontFamily: "'Dancing Script', cursive", color: light ? '#F5C518' : '#2478C8' }}
      >
        {subtitle}
      </p>
    )}
    <div className="flex items-center justify-center gap-3 mt-5">
      <span className="w-12 h-[1px]" style={{ background: '#F5C518' }} />
      <span className="w-2 h-2 rotate-45" style={{ background: '#F5C518' }} />
      <span className="w-12 h-[1px]" style={{ background: '#F5C518' }} />
    </div>
  </div>
);

// ============= ABOUT SECTION =============
export const AboutSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const handleScrollToRooms = () => {
    const el = document.getElementById('rooms');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBtnEnter = (e) => {
    e.currentTarget.style.background = '#F5C518';
    e.currentTarget.style.color = '#1B3D6E';
  };

  const handleBtnLeave = (e) => {
    e.currentTarget.style.background = '#1B3D6E';
    e.currentTarget.style.color = '#FFFFFF';
  };

  return (
  <section id="about" className="py-20 lg:py-28 overflow-hidden" style={{ background: '#FFFFFF' }}>
  <div className="max-w-7xl mx-auto px-4 lg:px-8">
    <SectionTitle title="About Dreamland Inn" subtitle="Your seaside escape" />
    <div ref={ref} className="grid lg:grid-cols-2 gap-12 items-center mt-8">

      {/* Image */}
      <div
        className="relative pb-8"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateX(0)' : 'translateX(-50px)',
          transition: 'all 0.8s ease-out',
        }}
      >
        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
          <img
            src={aboutData.image}
            alt="Dreamland Inn Resort"
            className="w-full h-[400px] lg:h-[500px] object-cover"
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(27,61,110,0.35), transparent)' }}
          />
        </div>

        {/* Badge — outside overflow-hidden wrapper */}
        <div
          className="absolute -bottom-6 right-4 lg:right-8 p-6 rounded-xl shadow-xl"
          style={{ background: '#F5C518' }}
        >
          <p
            className="text-3xl font-bold"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1B3D6E' }}
          >
            14+
          </p>
          <p className="text-xs tracking-wider uppercase" style={{ color: '#1B3D6E' }}>
            Years of<br />Experience
          </p>
        </div>
      </div>

      {/* Text */}
      <div
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateX(0)' : 'translateX(50px)',
          transition: 'all 0.8s ease-out 0.2s',
        }}
      >
        {aboutData.paragraphs.map((p, i) => (
          <p key={i} className="leading-relaxed mb-5 text-base" style={{ color: '#4A5568' }}>
            {p}
          </p>
        ))}
        <button
          onClick={handleScrollToRooms}
          onMouseEnter={handleBtnEnter}
          onMouseLeave={handleBtnLeave}
          className="inline-block mt-4 px-8 py-3 text-sm font-semibold tracking-wider rounded transition-all duration-300"
          style={{ background: '#1B3D6E', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
        >
          View Our Rooms
        </button>
      </div>

    </div>
  </div>
</section>
  );
};



const FoodCard = ({ item, index, inView }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className="group relative rounded-2xl overflow-hidden shadow-lg cursor-pointer will-change-transform"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.6s ease, transform 0.6s ease ${index * 0.1}s`,
        boxShadow: '0 8px 32px rgba(27,61,110,0.15)',
      }}
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden" style={{ background: '#C5D9F0' }}>
        {!loaded && (
          <div className="absolute inset-0 animate-pulse" style={{ background: '#C5D9F0' }} />
        )}
        <img
          src={item.image}
          alt={item.title}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${
            loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-110 blur-md'
          }`}
        />
      </div>
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to top, rgba(27,61,110,0.85) 0%, rgba(27,61,110,0.15) 55%, transparent 100%)' }}
      />
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3
          className="text-2xl font-bold text-white mb-1"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {item.title}
        </h3>
        <p className="text-sm" style={{ color: '#F5C518' }}>
          {item.description}
        </p>
      </div>
    </div>
  );
};

// ============= FOOD SECTION =============
export const FoodSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    foodItems.slice(0, 3).forEach((item) => {
      const img = new Image();
      img.src = item.image;
    });
  }, []);

  return (
    <section className="py-20 lg:py-28" style={{ background: '#EEF6FF' }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <SectionTitle title="Konkani Authentic Foods" subtitle="Taste of the Konkan coast" />
        <div ref={ref} className="grid md:grid-cols-3 gap-8 mt-8">
          {foodItems.map((item, index) => (
            <FoodCard key={item.id} item={item} index={index} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const RoomCard = ({ room, index, inView, amenityIcons, onBook }) => {
  const [current, setCurrent] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [allReady, setAllReady] = useState(false);
  const [hovered, setHovered] = useState(false);
  const images = useMemo(() => room.images || [room.image], [room]);

  // ─── Preload all images on mount ───────────────────────────────────────────
  useEffect(() => {
    let firstReady = false;
    const imgs = images.map((src, i) => {
      const img = new window.Image();
      img.src = src;
      const onDone = () => {
        setLoadedCount(c => c + 1);
        if (i === 0 && !firstReady) {
          firstReady = true;
          setAllReady(true);
        }
      };
      img.onload = onDone;
      img.onerror = onDone;
      if (img.complete) onDone();
      return img;
    });
    return () => imgs.forEach(img => { img.onload = null; img.onerror = null; });
  }, [images]);

  // ─── Autoplay — pauses on hover ───────────────────────────────────────────
  useEffect(() => {
    if (!allReady || hovered || images.length <= 1) return;
    const t = setInterval(() => {
      setCurrent(i => (i + 1) % images.length);
    }, 3500);
    return () => clearInterval(t);
  }, [allReady, hovered, images.length]);

  const isLoaded = loadedCount > 0;
  const prev = () => setCurrent(i => (i - 1 + images.length) % images.length);
  const next = () => setCurrent(i => (i + 1) % images.length);

  // ─── Hover handlers ───────────────────────────────────────────────────────
  const handleArrowEnter = e => {
    e.currentTarget.style.background = '#F5C518';
    e.currentTarget.style.color = '#1B3D6E';
  };
  const handleArrowLeave = e => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
    e.currentTarget.style.color = '#fff';
  };
  const handleAmenityEnter = e => {
    e.currentTarget.style.background = '#D6E8FA';
    e.currentTarget.style.borderColor = '#2478C8';
  };
  const handleAmenityLeave = e => {
    e.currentTarget.style.background = '#EEF6FF';
    e.currentTarget.style.borderColor = '#C5D9F0';
  };
  const handleBookEnter = e => {
    e.currentTarget.style.background = 'linear-gradient(135deg, #F5C518 0%, #D4A010 100%)';
    e.currentTarget.style.color = '#1B3D6E';
    e.currentTarget.style.boxShadow = '0 6px 24px rgba(245,197,24,0.45)';
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const handleBookLeave = e => {
    e.currentTarget.style.background = 'linear-gradient(135deg, #2478C8 0%, #1B3D6E 100%)';
    e.currentTarget.style.color = '#fff';
    e.currentTarget.style.boxShadow = '0 4px 16px rgba(27,61,110,0.35)';
    e.currentTarget.style.transform = 'translateY(0)';
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="rounded-2xl overflow-hidden transition-all duration-500 group"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(32px)',
        transitionDelay: `${index * 140}ms`,
        boxShadow: '0 8px 40px rgba(27,61,110,0.15), 0 2px 8px rgba(27,61,110,0.08)',
      }}
    >
      {/* ── IMAGE SLIDESHOW ── */}
      <div className="relative h-56 overflow-hidden" style={{ background: '#1B3D6E' }}>

        {/* Skeleton shimmer */}
        <div
          className="absolute inset-0 z-20 transition-opacity duration-500"
          style={{
            opacity: allReady ? 0 : 1,
            pointerEvents: 'none',
            background: 'linear-gradient(90deg, #1B3D6E 25%, #2478C8 50%, #1B3D6E 75%)',
            backgroundSize: '200% 100%',
            animation: allReady ? 'none' : 'shimmer 1.4s infinite',
          }}
        />

        {/* Images */}
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`${room.name} ${i + 1}`}
            loading={i === 0 ? 'eager' : 'lazy'}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              willChange: 'opacity',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
              opacity: i === current && isLoaded ? 1 : 0,
              transform: i === current ? 'scale(1)' : 'scale(1.04)',
            }}
          />
        ))}

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to bottom, rgba(27,61,110,0.15) 0%, rgba(27,61,110,0) 40%, rgba(27,61,110,0.78) 100%)',
          }}
        />

        {room.badge && (
          <span
            className="absolute top-3 left-3 z-10 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full"
            style={{ background: '#F5C518', color: '#1B3D6E' }}
          >
            {room.badge}
          </span>
        )}

        <span
          className="absolute top-3 right-3 z-10 text-white text-xs font-medium px-2.5 py-1 rounded-full"
          style={{ background: 'rgba(27,61,110,0.60)', backdropFilter: 'blur(6px)' }}
        >
          {current + 1} / {images.length}
        </span>

        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              onMouseEnter={handleArrowEnter}
              onMouseLeave={handleArrowLeave}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center text-white text-lg font-bold opacity-0 group-hover:opacity-100 transition-all duration-200"
              style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.3)',
                backdropFilter: 'blur(4px)',
              }}
            >
              ‹
            </button>
            <button
              onClick={next}
              onMouseEnter={handleArrowEnter}
              onMouseLeave={handleArrowLeave}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center text-white text-lg font-bold opacity-0 group-hover:opacity-100 transition-all duration-200"
              style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.3)',
                backdropFilter: 'blur(4px)',
              }}
            >
              ›
            </button>
          </>
        )}

        <div className="absolute bottom-0 left-0 right-0 z-10 px-4 pb-4 pt-2">
          <h3
            className="text-[22px] font-bold leading-tight text-white"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
          >
            {room.name}
          </h3>
          <div className="flex items-center justify-between mt-1.5">
            {room.beds && (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full"
                style={{
                  background: 'rgba(245,197,24,0.22)',
                  border: '1px solid rgba(245,197,24,0.55)',
                  color: '#FFF0A0',
                }}
              >
                🛏 {room.beds}
              </span>
            )}
            {images.length > 1 && (
              <div className="flex gap-1.5 items-center">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className="rounded-full border-0 p-0 transition-all duration-200"
                    style={{
                      width: i === current ? '18px' : '6px',
                      height: '6px',
                      background: i === current ? '#F5C518' : 'rgba(255,255,255,0.45)',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── CARD BODY ── */}
      <div className="px-5 pt-4 pb-5" style={{ background: '#FFFFFF' }}>
        {room.type && (
          <div className="mb-3">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full"
              style={{ background: '#EEF6FF', border: '1px solid #C5D9F0', color: '#1B3D6E' }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#2478C8" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                <circle cx="12" cy="9" r="2.5" />
              </svg>
              {room.type}
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          {room.amenities.map((item, i) => {
            const Icon = amenityIcons[item];
            return (
              <span
                key={i}
                onMouseEnter={handleAmenityEnter}
                onMouseLeave={handleAmenityLeave}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors duration-150 cursor-default"
                style={{ background: '#EEF6FF', border: '1px solid #C5D9F0', color: '#1B3D6E' }}
              >
                {Icon && <Icon size={12} />}
                {item}
              </span>
            );
          })}
        </div>

        <div className="mb-4 -mx-5" style={{ height: '16px' }}>
          <svg viewBox="0 0 440 16" preserveAspectRatio="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,8 C55,16 110,0 165,8 C220,16 275,0 330,8 C385,16 418,3 440,8" fill="none" stroke="#C5D9F0" strokeWidth="1.5" />
          </svg>
        </div>

        <div
          className="flex rounded-2xl overflow-hidden mb-4"
          style={{ border: '1px solid #C5D9F0', background: '#EEF6FF' }}
        >
          {room.price.ac && (
            <div className="flex-1 px-4 py-3">
              <span className="block text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#2478C8' }}>
                AC / night
              </span>
              <span
                className="text-[22px] font-bold leading-tight"
                style={{ color: '#F5C518', fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                ₹{room.price.ac.toLocaleString('en-IN')}
              </span>
            </div>
          )}
          {room.price.nonAc && (
            <div
              className="flex-1 px-4 py-3"
              style={room.price.ac ? { borderLeft: '1px solid #C5D9F0' } : {}}
            >
              <span className="block text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#2478C8' }}>
                Non-AC / night
              </span>
              <span
                className="text-[22px] font-bold leading-tight"
                style={{ color: '#F5C518', fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                ₹{room.price.nonAc.toLocaleString('en-IN')}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={onBook}
          onMouseEnter={handleBookEnter}
          onMouseLeave={handleBookLeave}
          className="w-full py-3 rounded-2xl text-sm font-semibold text-white relative overflow-hidden transition-all duration-200"
          style={{
            background: 'linear-gradient(135deg, #2478C8 0%, #1B3D6E 100%)',
            boxShadow: '0 4px 16px rgba(27,61,110,0.35)',
            letterSpacing: '0.04em',
          }}
        >
          🌴 Inquiry / Book Now
        </button>
      </div>
    </div>
  );
};

// ============= ROOMS SECTION =============
export const RoomsSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [selectedRoom, setSelectedRoom] = useState(null);

  const amenityIcons = useMemo(() => ({ 'Sea View': Eye, AC: Wind, 'Wi-Fi': Wifi }), []);

  return (
    <section id="rooms" className="py-20 lg:py-28" style={{ background: '#FFFFFF' }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <SectionTitle title="Our Rooms & Cottages" subtitle="Beachfront living at its finest" />
        <div ref={ref} className="grid md:grid-cols-2 gap-8 mt-8">
          {rooms.map((room, index) => (
            <RoomCard
              key={room.id}
              room={room}
              index={index}
              inView={inView}
              amenityIcons={amenityIcons}
              onBook={() => setSelectedRoom(room)}
            />
          ))}
        </div>
        {selectedRoom && (
          <BookingModal selectedRoom={selectedRoom} onClose={() => setSelectedRoom(null)} />
        )}
      </div>
    </section>
  );
};

// ============= DESTINATION EVENTS SECTION =============
export const EventsSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const handleTagEnter = (e) => {
    e.currentTarget.style.background = '#F5C518';
    e.currentTarget.style.color = '#1B3D6E';
    e.currentTarget.style.borderColor = '#F5C518';
  };

  const handleTagLeave = (e) => {
    e.currentTarget.style.background = '#FFFFFF';
    e.currentTarget.style.color = '#1B3D6E';
    e.currentTarget.style.borderColor = '#C5D9F0';
  };

  return (
    <section id="events" className="py-20 lg:py-28" style={{ background: '#EEF6FF' }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <SectionTitle
          title="Destination Events"
          subtitle="Every occasion deserves a setting worth remembering"
        />

        <div ref={ref} className="grid md:grid-cols-3 gap-8 mt-8">
          {destinationEvents.map((event, index) => (
            <div
              key={event.id}
              className="group relative rounded-2xl overflow-hidden h-96 cursor-pointer"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `all 0.7s ease-out ${index * 0.1}s`,
                boxShadow: '0 8px 32px rgba(27,61,110,0.18)',
              }}
            >
              <img
                src={event.image}
                alt={event.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div
                className="absolute inset-0 transition-all duration-500"
                style={{ background: 'linear-gradient(to top, rgba(27,61,110,0.88) 0%, rgba(27,61,110,0.25) 55%, transparent 100%)' }}
              />
              <div
                className="absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: '#F5C518' }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <h3
                  className="text-2xl font-bold text-white mb-2"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {event.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
                  {event.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <p className="text-xs uppercase tracking-widest mb-4 font-medium" style={{ color: '#2478C8' }}>
            And many more
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {allEventTypes.map((type) => (
              <span
                key={type}
                onMouseEnter={handleTagEnter}
                onMouseLeave={handleTagLeave}
                className="px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-default"
                style={{ border: '1px solid #C5D9F0', color: '#1B3D6E', background: '#FFFFFF' }}
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============= GALLERY SECTION =============


export const GallerySection = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.01 });

  const filters = ["all", "resort", "beach", "rooms", "food"];

  const filtered =
    activeFilter === "all"
      ? galleryImages
      : galleryImages.filter((img) => img.category === activeFilter);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight")
        setLightboxIndex((i) => (i + 1) % filtered.length);
      if (e.key === "ArrowLeft")
        setLightboxIndex((i) => (i - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, filtered.length]);

  useEffect(() => {
    document.body.style.overflow = lightboxIndex !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [lightboxIndex]);

  const lightboxImage = lightboxIndex !== null ? filtered[lightboxIndex] : null;

  return (
    <section id="gallery" className="py-20 lg:py-28" style={{ background: "#FFFFFF" }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <SectionTitle title="Photo Gallery" subtitle="Glimpses of paradise" />

        {/* ── Filter Bar ── */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {filters.map((filter) => {
            const isActive = activeFilter === filter;
            return (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className="px-5 py-2 rounded-full text-sm font-medium capitalize transition-all duration-300"
                style={
                  isActive
                    ? {
                        background: "#1B3D6E",
                        color: "#F5C518",
                        boxShadow: "0 4px 16px rgba(27,61,110,0.28)",
                        border: "1.5px solid #1B3D6E",
                      }
                    : {
                        background: "#F4F8FF",
                        color: "#1B3D6E",
                        border: "1px solid #C5D9F0",
                      }
                }
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "#2478C8";
                    e.currentTarget.style.color = "#FFFFFF";
                    e.currentTarget.style.borderColor = "#2478C8";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "#F4F8FF";
                    e.currentTarget.style.color = "#1B3D6E";
                    e.currentTarget.style.borderColor = "#C5D9F0";
                  }
                }}
              >
                {filter}
              </button>
            );
          })}
        </div>

        {/* ── Auto-shape Grid ── */}
        <div
          ref={ref}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "14px",
          }}
        >
          {filtered.map((image, index) => {
            // landscape images span 2 columns, portrait/square stay at 1
            const isLandscape = image.landscape === true;

            return (
              <div
                key={image.id}
                className="group relative rounded-2xl overflow-hidden cursor-pointer"
                onClick={() => setLightboxIndex(index)}
                style={{
                  gridColumn: isLandscape ? "span 2" : "span 1",
                  // landscape = 16:9, square = 1:1
                  aspectRatio: isLandscape ? "16/9" : "1/1",
                  opacity: inView ? 1 : 0,
                  transform: inView ? "translateY(0)" : "translateY(20px)",
                  transition: `opacity 0.5s ease ${index * 0.055}s, transform 0.5s ease ${index * 0.055}s`,
                  boxShadow: "0 2px 12px rgba(27,61,110,0.10)",
                }}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />

                {/* Overlay */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                  style={{ background: "rgba(27,61,110,0.62)" }}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center mb-3 transform scale-75 group-hover:scale-100 transition-transform duration-300"
                    style={{ border: "1.5px solid rgba(245,197,24,0.7)" }}
                  >
                    <ZoomIn size={16} color="#F5C518" />
                  </div>
                  <div
                    className="w-8 h-[2px] mb-2"
                    style={{ background: "#F5C518" }}
                  />
                  <span className="text-xs font-medium text-white text-center px-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 leading-snug">
                    {image.alt}
                  </span>
                  {/* Category badge */}
                  <span
                    className="absolute top-3 left-3 text-[10px] font-semibold uppercase px-2 py-[3px] rounded-full tracking-wider"
                    style={{
                      background: "rgba(245,197,24,0.18)",
                      color: "#F5C518",
                      border: "1px solid rgba(245,197,24,0.35)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    {image.category}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Lightbox ── */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(8,16,34,0.96)" }}
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close */}
          <button
            className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              background: "rgba(245,197,24,0.12)",
              border: "1px solid rgba(245,197,24,0.35)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(245,197,24,0.26)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(245,197,24,0.12)")}
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            aria-label="Close"
          >
            <X size={18} color="#F5C518" />
          </button>

          {/* Prev */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              background: "rgba(245,197,24,0.10)",
              border: "1px solid rgba(245,197,24,0.30)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(245,197,24,0.24)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(245,197,24,0.10)")}
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((i) => (i - 1 + filtered.length) % filtered.length);
            }}
            aria-label="Previous"
          >
            <ChevronLeft size={20} color="#F5C518" />
          </button>

          {/* Next */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              background: "rgba(245,197,24,0.10)",
              border: "1px solid rgba(245,197,24,0.30)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(245,197,24,0.24)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(245,197,24,0.10)")}
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((i) => (i + 1) % filtered.length);
            }}
            aria-label="Next"
          >
            <ChevronRight size={20} color="#F5C518" />
          </button>

          {/* Image */}
          <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              className="max-w-[85vw] max-h-[78vh] object-contain rounded-xl"
              style={{ boxShadow: "0 0 60px rgba(0,0,0,0.5)" }}
            />
            <div className="mt-5 flex items-center gap-4">
              <span className="text-sm font-medium tracking-wide" style={{ color: "#F5C518" }}>
                {lightboxImage.alt}
              </span>
              <span
                className="text-xs px-3 py-1 rounded-full"
                style={{
                  background: "rgba(245,197,24,0.12)",
                  color: "rgba(245,197,24,0.65)",
                  border: "1px solid rgba(245,197,24,0.2)",
                }}
              >
                {lightboxIndex + 1} / {filtered.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};